import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './Contact.module.css';

// Initialize EmailJS (you'll need to update these credentials)
const initEmailJS = () => {
  const script = document.createElement('script');
  script.src = 'https://cdn.jsdelivr.net/npm/@emailjs/browser@3/dist/build/index.min.js';
  script.onload = () => {
    window.emailjs.init('SERVICE_ID_PLACEHOLDER');
  };
  document.body.appendChild(script);
};

// Department names the booking API expects, in the same order as contact.departments in the locale files.
const BOOKING_DEPARTMENTS = [
  'General Pediatrics', 'Neonatology', 'Child Neurology', 'Pediatric Allergology', 'Pediatric Gastroenterology',
  'Pediatric Nephrology', 'Pediatric Endocrinology', 'Pediatric Rheumatology', 'Instrumental Diagnosis',
  'Pediatric Pulmonology', 'Adult Medicine',
];
const BOOKING_DAYS_AHEAD = 60;

const isoDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const Contact = () => {
  const { t, i18n } = useTranslation();
  const inputLocaleMap = { hy: 'hy-AM', en: 'en-US', ru: 'ru-RU' };
  const inputLang = inputLocaleMap[i18n.language] || 'en-US';
  const dateInputRef = useRef(null);
  const timeInputRef = useRef(null);

  const handleDateClick = (e) => {
    e.stopPropagation();
    if (dateInputRef.current) {
      dateInputRef.current.focus();
    }
  };

  const handleTimeClick = (e) => {
    e.stopPropagation();
    if (timeInputRef.current) {
      timeInputRef.current.focus();
    }
  };

  const departments = t('contact.departments', { returnObjects: true });
  const contactItems = [
    { icon: '📍', label: t('contact.address'), info: t('contact.addressInfo') },
    { icon: '📞', label: t('contact.phone'), info: t('contact.phoneInfo') },
    { icon: '✉️', label: t('contact.email'), info: t('contact.emailInfo') },
    { icon: '🕐', label: t('contact.hoursLabel'), info: t('contact.hoursInfo') },
    { icon: '🌐', label: t('contact.languagesLabel'), info: t('contact.languagesInfo') },
  ];
  const [form, setForm] = useState({ childName: '', parentName: '', phone: '', age: '', department: '', date: '', time: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  // Free appointment times from the patient portal (/api/public/slots).
  // 'unavailable' means the portal can't be reached, so the form falls back to a free time field.
  const [slots, setSlots] = useState({ state: 'idle', list: [] });

  const today = new Date();
  const minDate = isoDate(today);
  const maxDate = isoDate(new Date(today.getFullYear(), today.getMonth(), today.getDate() + BOOKING_DAYS_AHEAD));
  const bookingDepartment = BOOKING_DEPARTMENTS[Number(form.department)];

  useEffect(() => {
    initEmailJS();
  }, []);

  const loadSlots = useCallback(async (date, department) => {
    if (!date || !department) return setSlots({ state: 'idle', list: [] });
    setSlots((s) => ({ ...s, state: 'loading' }));
    try {
      const res = await fetch(`/api/public/slots?date=${date}&department=${encodeURIComponent(department)}`);
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      setSlots({ state: 'ok', list: data.slots });
    } catch {
      setSlots({ state: 'unavailable', list: [] });
    }
  }, []);

  useEffect(() => {
    loadSlots(form.date, bookingDepartment);
  }, [form.date, bookingDepartment, loadSlots]);

  const handleChange = (e) => {
    const next = { ...form, [e.target.name]: e.target.value };
    if (e.target.name === 'date' || e.target.name === 'department') next.time = ''; // free times differ per day and department
    setForm(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const done = () => {
      setSubmitted(true);
      setTimeout(() => setSubmitted(false), 4000);
      setForm({ childName: '', parentName: '', phone: '', age: '', department: '', date: '', time: '' });
      setSlots({ state: 'idle', list: [] });
    };

    try {
      // 1. Save the request in the patient portal, which also checks the time is still free.
      let saved = false;
      if (slots.state === 'ok') {
        const booking = await fetch('/api/public/bookings', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            parent_name: form.parentName,
            child_name: form.childName,
            phone: form.phone,
            child_age: form.age,
            department: bookingDepartment,
            date: form.date,
            time: form.time,
            lang: i18n.language,
          }),
        }).catch(() => null);
        if (booking && booking.status === 409) {
          setError(t('contact.slotTaken'));
          setForm({ ...form, time: '' });
          loadSlots(form.date, bookingDepartment);
          return;
        }
        // Any other problem: the email below still reaches the clinic, so the request isn't lost.
        saved = !!booking && booking.ok;
      }

      // 2. Email the clinic, as before.
      const response = await fetch('https://eak-clinic-backend.onrender.com/api/send-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          childName: form.childName,
          parentName: form.parentName,
          phone: form.phone,
          age: form.age,
          department: departments[Number(form.department)] || '',
          date: form.date,
          time: form.time
        })
      }).catch((err) => {
        if (saved) return null;
        throw err;
      });
      const data = response ? await response.json().catch(() => ({})) : {};

      if (saved || (response && response.ok)) {
        done();
      } else {
        setError(`Failed: ${data.message || 'Unknown error'}`);
        console.error('Server error:', data);
      }
    } catch (err) {
      setError(`Error: ${err.message}`);
      console.error('Request error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className={styles.section}>
      <div className={styles.sectionHeader}>
        <p className={styles.label}>{t('contact.label')}</p>
        <h2 className={styles.title}>{t('contact.title')}</h2>
        <p className={styles.desc}>{t('contact.desc')}</p>
      </div>
      <div className={styles.left}>
        <div className={styles.contactItems}>
          {contactItems.map(({ icon, label, info }) => (
            <div key={label} className={styles.contactItem}>
              <div className={styles.contactIcon}>{icon}</div>
              <div className={styles.contactInfo}>
                <strong>{label}</strong>
                {info}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className={styles.right}>
        <form className={styles.form} onSubmit={handleSubmit}>
          <h3 className={styles.formTitle}>{t('contact.formTitle')}</h3>

          {submitted && (
            <div className={styles.success}>
              {t('contact.successMsg')}
            </div>
          )}

          {error && (
            <div className={styles.error} style={{ color: '#e74c3c', padding: '10px', backgroundColor: '#fadbd8', borderRadius: '4px', marginBottom: '1rem' }}>
              {error}
            </div>
          )}

          <div className={styles.row}>
            <input
              className={styles.input}
              type="text"
              name="childName"
              placeholder={t('contact.childNamePlaceholder')}
              value={form.childName}
              onChange={handleChange}
              required
            />
            <input
              className={styles.input}
              type="text"
              name="parentName"
              placeholder={t('contact.parentNamePlaceholder')}
              value={form.parentName}
              onChange={handleChange}
              required
            />
          </div>
          <div className={styles.row}>
            <input
              className={styles.input}
              type="tel"
              name="phone"
              placeholder={t('contact.phonePlaceholder')}
              value={form.phone}
              onChange={handleChange}
              required
            />
            <input
              className={styles.input}
              type="text"
              name="age"
              placeholder={t('contact.agePlaceholder')}
              value={form.age}
              onChange={handleChange}
              required
            />
          </div>
          <select
            className={styles.input}
            name="department"
            value={form.department}
            onChange={handleChange}
            required
          >
            <option value="" disabled>{t('contact.selectDept')}</option>
            {departments.map((d, i) => <option key={d} value={i}>{d}</option>)}
          </select>
          <div className={styles.row}>
            <input
              ref={dateInputRef}
              className={styles.input}
              type="date"
              lang={inputLang}
              name="date"
              min={minDate}
              max={maxDate}
              value={form.date}
              onChange={handleChange}
              onClick={handleDateClick}
              style={{ cursor: 'pointer' }}
              required
            />
            {slots.state === 'unavailable' ? (
              <input
                ref={timeInputRef}
                className={styles.input}
                type="time"
                lang={inputLang}
                name="time"
                value={form.time}
                onChange={handleChange}
                onClick={handleTimeClick}
                style={{ cursor: 'pointer' }}
                required
              />
            ) : (
              <select
                className={styles.input}
                name="time"
                value={form.time}
                onChange={handleChange}
                disabled={slots.state !== 'ok' || !slots.list.length}
                required
              >
                <option value="" disabled>
                  {slots.state === 'idle' ? t('contact.chooseDateFirst')
                    : slots.state === 'loading' ? t('contact.loadingSlots')
                    : slots.list.length ? t('contact.selectTime') : t('contact.noSlots')}
                </option>
                {slots.list.map((time) => <option key={time} value={time}>{time}</option>)}
              </select>
            )}
          </div>
          <button type="submit" className={styles.submit} disabled={loading}>
            {loading ? t('contact.sending') : t('contact.submitBtn')}
          </button>
        </form>
      </div>
      <div className={styles.mapCard}>
        <iframe
          title="Clinic location map"
          className={styles.map}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          src="https://www.google.com/maps?q=11%2F4%20Argishti%20Street%2C%20Yerevan%2C%20Armenia&output=embed"
        />
      </div>
    </section>
  );
};

export default Contact;
